import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { SCREENS_ATIVAS, type ScreenKey } from '@apen/shared';
import { useSession } from './hooks/useSession';
import { useCarregamentoSuave } from './hooks/useCarregamentoSuave';
import { AppShell } from './components/Shell/AppShell';
import { LiquidLoader } from './components/Loader/LiquidLoader';
import Login from './pages/Auth/Login';

const EsqueciSenha = lazy(() => import('./pages/Auth/EsqueciSenha'));
const CriarSenha = lazy(() => import('./pages/Auth/CriarSenha'));

/** Telas do painel, carregadas sob demanda. */
const PaginaHome = lazy(() => import('./pages/Home/Home'));
const PaginaExtratos = lazy(() => import('./pages/Extratos/Extratos'));
const PaginaClassificacao = lazy(() => import('./pages/Classificacao/Classificacao'));
const PaginaPlano = lazy(() => import('./pages/Plano/PlanoFinanceiro'));
const PaginaPlanilhas = lazy(() => import('./pages/Planilhas/Planilhas'));
const PaginaBancosContabeis = lazy(() => import('./pages/BancosContabeis/BancosContabeis'));
const PaginaPatrimoniais = lazy(() => import('./pages/Patrimoniais/ContasPatrimoniais'));
const PaginaExportacao = lazy(() => import('./pages/Exportacao/ExportacaoContabil'));
const PaginaRelatorios = lazy(() => import('./pages/Relatorios/Relatorios'));
const PaginaBalanco = lazy(() => import('./pages/Balanco/Balanco'));
const PaginaLog = lazy(() => import('./pages/Log/Log'));
const PaginaCompetencias = lazy(() => import('./pages/Competencias/Competencias'));
const PaginaUsuarios = lazy(() => import('./pages/Usuarios/Usuarios'));
const PaginaBancos = lazy(() => import('./pages/Bancos/Bancos'));
const PaginaEmpresas = lazy(() => import('./pages/Empresas/Empresas'));
const PaginaPerfil = lazy(() => import('./pages/Perfil/Perfil'));

/**
 * Porteiro das rotas privadas.
 *
 * Enquanto a sessao esta sendo verificada nao decidimos nada: renderizar o
 * login antes da resposta de `/api/me` faria a tela piscar em toda navegacao
 * de quem ja esta logado.
 */
function Privado({ children }: { children: React.ReactNode }) {
  const { data: session, isLoading } = useSession();
  const location = useLocation();

  // Só a verificação inicial da sessão ocupa a tela inteira — é o único
  // momento em que ainda não existe painel para mostrar por baixo.
  const mostrarLoader = useCarregamentoSuave(isLoading);

  if (isLoading) return mostrarLoader ? <LiquidLoader fixo /> : null;

  if (!session) {
    // Guarda de onde a pessoa veio, para voltar ao destino depois do login.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  return <>{children}</>;
}

/**
 * Rota de tela modulavel.
 *
 * Redireciona para a primeira tela que a conta realmente tem, e nao para "/"
 * fixo: quem nao tem a Home liberada entraria num laco de redirecionamento
 * batendo sempre na mesma porta fechada.
 *
 * Isto e conveniencia de navegacao, nao seguranca — a API confere a mesma
 * permissao antes de responder qualquer rota da tela.
 */
function SomenteTela({ tela, children }: { tela: ScreenKey; children: React.ReactNode }) {
  const { data: session } = useSession();
  if (!session) return <>{children}</>;
  if (session.screens.includes(tela)) return <>{children}</>;

  const primeira = SCREENS_ATIVAS.find((item) => session.screens.includes(item.key));
  return <Navigate to={primeira?.path ?? '/perfil'} replace />;
}

/** So o admin geral: cadastro de empresas e quem acessa cada uma. */
function SomenteAdminGeral({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  if (session && !session.admin_geral) return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Rotas restritas a admin: usuarios e as travas de periodo. */
function SomenteAdmin({ children }: { children: React.ReactNode }) {
  const { data: session } = useSession();
  if (session && session.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    /**
     * Este Suspense cobre apenas as rotas públicas, que são telas inteiras e
     * não têm casca por baixo.
     *
     * As rotas do painel têm o próprio limite de Suspense DENTRO do AppShell,
     * em volta do <Outlet />. Antes elas caíam neste boundary aqui, e o efeito
     * era que trocar de tela desmontava o painel inteiro — barra lateral e
     * cabeçalho sumiam e voltavam a cada clique.
     */
    <Suspense fallback={<LiquidLoader fixo />}>
      <Routes>
        {/* Publicas */}
        <Route path="/login" element={<Login />} />
        <Route path="/esqueci-senha" element={<EsqueciSenha />} />
        <Route path="/criar-senha" element={<CriarSenha />} />

        {/* Privadas, dentro do shell com navegacao */}
        <Route
          element={
            <Privado>
              <AppShell />
            </Privado>
          }
        >
          <Route
            path="/"
            element={
              <SomenteTela tela="home">
                <PaginaHome />
              </SomenteTela>
            }
          />
          <Route
            path="/extratos"
            element={
              <SomenteTela tela="extratos">
                <PaginaExtratos />
              </SomenteTela>
            }
          />
          <Route
            path="/classificacao"
            element={
              <SomenteTela tela="classificacao">
                <PaginaClassificacao />
              </SomenteTela>
            }
          />
          <Route
            path="/plano"
            element={
              <SomenteTela tela="plano">
                <PaginaPlano />
              </SomenteTela>
            }
          />
          <Route
            path="/planilhas"
            element={
              <SomenteTela tela="planilhas">
                <PaginaPlanilhas />
              </SomenteTela>
            }
          />
          <Route
            path="/bancos-contabeis"
            element={
              <SomenteTela tela="bancos_contabeis">
                <PaginaBancosContabeis />
              </SomenteTela>
            }
          />
          <Route
            path="/patrimoniais"
            element={
              <SomenteTela tela="patrimoniais">
                <PaginaPatrimoniais />
              </SomenteTela>
            }
          />
          <Route
            path="/exportacao"
            element={
              <SomenteTela tela="exportacao">
                <PaginaExportacao />
              </SomenteTela>
            }
          />
          <Route
            path="/relatorios"
            element={
              <SomenteTela tela="relatorios">
                <PaginaRelatorios />
              </SomenteTela>
            }
          />
          <Route
            path="/balanco"
            element={
              <SomenteTela tela="balanco">
                <PaginaBalanco />
              </SomenteTela>
            }
          />
          <Route
            path="/competencias"
            element={
              <SomenteAdmin>
                <PaginaCompetencias />
              </SomenteAdmin>
            }
          />
          <Route
            path="/log"
            element={
              <SomenteAdmin>
                <PaginaLog />
              </SomenteAdmin>
            }
          />
          <Route
            path="/usuarios"
            element={
              <SomenteAdmin>
                <PaginaUsuarios />
              </SomenteAdmin>
            }
          />
          <Route
            path="/bancos"
            element={
              <SomenteAdmin>
                <PaginaBancos />
              </SomenteAdmin>
            }
          />
          <Route
            path="/empresas"
            element={
              <SomenteAdminGeral>
                <PaginaEmpresas />
              </SomenteAdminGeral>
            }
          />
          <Route path="/perfil" element={<PaginaPerfil />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
